'use client';

import { useLocale } from 'next-intl';
import { ComponentPropsWithRef, ComponentRef, forwardRef } from 'react';

import { Link } from '@/i18n/routing';
import { defaultLocale as defaultLocaleCode } from '@/i18n/locales';
import { useHoverPrefetch } from '@/shared/hooks/use-hover-prefetch';
import { cn } from '@ecom/helper-react-class-combine';

type NextLinkProps = ComponentPropsWithRef<typeof Link>;

interface PrefetchOptions {
  prefetchOnHover?: boolean;
}

type Props = NextLinkProps & PrefetchOptions;

export const InternalLink = forwardRef<ComponentRef<'a'>, Props>(
  (
    { href, prefetch, prefetchOnHover = false, children, className, locale, onMouseEnter, ...rest },
    ref,
  ) => {
    const activeLocale = useLocale();
    const finalLocale = locale ?? activeLocale;
    const localeForLink = finalLocale === defaultLocaleCode ? undefined : finalLocale;
    const hoverPrefetch = useHoverPrefetch({ enabled: prefetchOnHover, prefetch, onMouseEnter });

    return (
      <Link
        className={cn(
          'focus-visible:ring-primary/20 hover:text-primary focus-visible:ring-4 focus-visible:outline-hidden',
          className,
        )}
        href={href}
        locale={localeForLink}
        onMouseEnter={hoverPrefetch.onMouseEnter}
        prefetch={hoverPrefetch.prefetch}
        ref={ref}
        {...rest}
      >
        {children}
      </Link>
    );
  },
);

InternalLink.displayName = 'InternalLink';
